import { BCMSDate, BCMSRecord, BCMSRecordProperty } from "@bcm-dev/bcms-vite";
import { Account, Autopay, Biller } from "../";
/**
* @class Payment
* @classdesc Class Class definition for record type from model
* @see {@link https://dev.oec.io/bcms/@bcm-dev/bcms-vite/-/tree/main/src/classes/BCMSRecord|BCMSRecord}
* @param {Class} record - record from model to init class
* @extends BCMSRecord
* @example const record = model.payments[0];
* const payment = new Payment(record);
* @property {account} _from - an instance of account to make payment from
* @property {biller} _to - the instance of biller to pay
* @property {bcmsCurrency} _amount- an instance of bcmsCurrency for amount of payment
* @property {String} _memo - memo of payment
* @property {bcmsDate} _date - an instance of bcmsDate for the day the payment is made
* @property {String} _status - the status of the payment
* @property {Number} _id - the id of the payment 
* @property {Boolean} _isActive - if the current payment is active
* @property {String} _message - message for the payment
* @property {String} _confirmationNumber - confirmation number for the payment
* @property {Boolean} _hasRelatedAutopay - If the current payment was created during adding an autopay
* @property {Autopay} _autopay - The autopay that created this payment, if this payment was created through autopay.
* @property {bcmsDate} _withdrawnOnDate - withdrawn on date
* @property {Boolean} hasImmediateWithdrawDate -  the value of wether the immediate withdraw date is true or not. 
This property is
related to branding options "Paper/Electronic Biller Expedited Payment Method", value "Real-Time Good Funds".
*/
export default class Payment extends BCMSRecord{
    _from;                      
    _to;                       
    _amount;                    
    _memo;                   
    _date;                      
    _status;                  
    _id;                       
    _isActive;                
    _message;                  
    _confirmationNumber;    
    _hasFee;
    _feeAmount;
    _hasRelatedAutopay;
    _autopay;
    _withdrawnOnDate;
    _hasImmediateWithdrawDate;

    constructor (record){
        super(record)
       
    } 

    // Returns a formatted date like "Oct 12" or "Oct 12, 2022"
    //  if todays year does not match the year passed.

    /**
     * @function formatDate
     * @instance
     * @returns {STRING}
     * @description Returns a formatted date string based on if the year is the same as the current or not
     * @memberof Payment
     */
    get formatDate() {
      let currentYear = BCMSDate.init().yearInt;
      var dateFormatted = this.date.format('M d');

      if(currentYear !== this.date.yearInt) {
            dateFormatted = this.date.format('M d, Y');
      }

      return dateFormatted;
   }
  
   /**
    * @function firstPayment
    * @instance
    * @param {OBJECT} model - The model object
    * @returns {Payment}
    * @description Returns the first payment from the model
    * @memberof Payment
    */
    static firstPayment(model){
        return  Payment.init(model.payments[0],model);
    }

   /**
    * @function getLastPaymentByBillerID
    * @instance
    * @param {OBJECT} model - The model object
    * @returns {Payment}
    * @description Returns the last payment for a biller by biller id. 
    * Currently used in pages like "Pay Bills", "Review and Pay", and "Scheduled Payments" to show messages
    * like "Last Payment $80.82...Processing Feb 22".
    * @memberof Payment
    */
   static getLastPaymentByBillerID(billerID, payments) {
      // Filter by payment status, the order of which
      // is "scheduled" followed by "processing" followed by "processed".
      function currentPaymentDateIsMoreRecentStatusThanLastPaymentDate(payment, lastPayment) {
         const currentPaymentStatus = payment.paymentStatus.toLowerCase();
         if(currentPaymentStatus === "scheduled") { 
            return currentPaymentDateIsMoreRecentLastPaymentDate(payment, lastPayment);
         }
         else if(currentPaymentStatus === "processing") {
            return currentPaymentDateIsMoreRecentLastPaymentDate(payment, lastPayment);
         }
         else if(currentPaymentStatus === "processed") {
            return currentPaymentDateIsMoreRecentLastPaymentDate(payment, lastPayment);
         } 
         else if(currentPaymentStatus === "canceled") {
            return currentPaymentDateIsMoreRecentLastPaymentDate(payment, lastPayment);
         }
      }

      // Filter by most recent payment.
      function currentPaymentDateIsMoreRecentLastPaymentDate(payment, lastPayment) {
            return parseInt(payment.paymentDate) >= parseInt(lastPayment.paymentDate);
      }

      const getLastPayment = billerID => Payment.initByID(
         payments.reduce((lastPayment, payment) => {
               return payment.paymentTo === billerID
               && (currentPaymentDateIsMoreRecentStatusThanLastPaymentDate(payment, lastPayment) || lastPayment.paymentDate === undefined)
               ? payment
               : lastPayment;
         }, {}).paymentID
      );

      return getLastPayment(billerID);
   }

   /**
    * @function getIfDateHasFee
    * @instance
    * @param {BCMSDate} date - A date we will use to see if it is a fee date.
    * @param {STRING} paymentType - The payment type for the current biller we are viewing.
    * @param {OBJECT} branding - The branding property of the model object. 
    * The "option" property in particular will be used.
    * @returns {BCMSDate[]} An array of bcms date instances.
    * @description Returns true if the argument "date" is a fee date.
    * @memberof Payment
    */
    static getIfDateHasFee(date, paymentType, branding) {
      // "virtualCard" biller types do not have fees.
      if(paymentType === "virtualCard") return false;
      return date.isEqual(Payment.getFeeDate(paymentType, branding, date));
    }

   /**
    * @function getFeeDate
    * @instance
    * @param {STRING} paymentType - The payment type for the current biller we are viewing.
    * @param {OBJECT} branding - The branding property of the model object. 
    * @param {BCMSDate} date - A date we will use to see if it is a fee date.
    * The "option" property in particular will be used.
    * @returns {BCMSDate} An instance of BCMSDate.
    * @description Returns an instance of BCMSDate, which is a fee date.
    * @memberof Payment
    */
    static getFeeDate(paymentType, branding, date) {
      let feeDate = BCMSDate.init();

      if(paymentType === "paper") { 
         feeDate.offset(1); 
      }

      if(feeDate.isWeekend()) { feeDate.nextBusinessDay(); }

      return feeDate;
   }

   /**
    * @function getWithdrawOnDate
    * @instance
    * @param {BCMSDate} date - The date which is the point of reference to get a withdraw date from.
    * This date is usually a selected date in a calendar and we are trying 
    * to get the appropriate withdraw date relative to that selected date.
    * @param {STRING} paymentType - The type of payment we are viewing, "paper" or "electronic".
    * @param {OBJECT} branding - The branding property of the model object. 
    * The "option" property in particular will be used.
    * @returns {BCMSDate} A bcms date object.
    * @description Returns a "withdraw on" date for a payment which is be determined by branding 
    * and the type of payment passed.
    * @memberof Payment
    */
   static getWithdrawOnDate(date, paymentType, branding) {
      if(typeof date == "string"){
         switch(date){
            case "Due Date":
               date = BCMSDate.init();
               break;
            default:
              date = BCMSDate.init();
         }
        
      }
      let paymentDate = date;
      const type = paymentType ?? "paper";
      const isPaper = type === "paper";
      const hasRealTimeGoodFunds = Payment.hasRealTimeGoodFunds(type, branding);

      if(hasRealTimeGoodFunds) {
         if(isPaper) {
            paymentDate = Payment.getPreviousBusinessDateFromDate(paymentDate, 5);
         // If today or tomorrow are selected, then "Withdrawn on today" should be shown.
         }  else if(paymentDate.isToday() || paymentDate.isEqual(BCMSDate.init().nextBusinessDay())) {
              paymentDate = BCMSDate.init();
          // Else if another future date is selected,
          // "Withdrawn on m/d/Y" (ex. Withdrawn on 05/21/2024)
          // should be shown which would be a business date.
          } else {
              paymentDate = Payment.getPreviousBusinessDateFromDate(paymentDate, 1);
          }
      }
      return paymentDate;
   }

   /**
    * @function getPreviousBusinessDateFromDate
    * @instance
    * @param {BCMSDate} date - The current date which is the point of reference to get a "previous" business date.
    * @param {String}  paymentType - The payment type either "paper" or "electronic".
    * The "option" property in particular will be used.
    * @returns {BCMSDate} - Returns a previous business date relative to the "date" passed.
    * @description - Returns a previous business date relative to the "date" passed.
    * @memberof Payment
    */
   static getPreviousBusinessDateFromDate(date, offset) {
      const paymentDate = BCMSDate.init(date.date);
      let counter = offset ?? 1;

      while(counter > 0) {
         paymentDate.offset(-1);
         while(paymentDate.isWeekend()) paymentDate.offset(-1);
         counter--;
      }

      return paymentDate;
   }

   /**
    * @function getBusinessDateOffset
    * @instance
    * @param {BCMSDate} date - The current date which is the point of reference to get a future business date offset.
    * @param {Number} offset - A positive number used to create a business date "x" days from the "date" passed.
    * The "option" property in particular will be used.
    * @returns {BCMSDate} - An instance of BCMSDate with "x" business days offset from the "date" passed.
    * @description - Returns an instance of BCMSDate with "x" business days offset from the "date" passed.
    * @memberof Payment
    */
   static getBusinessDateOffset(date, offset) {
      Array.apply(null, Array(offset)).forEach(i => date.nextBusinessDay());
      return date;
   }

   /**
    * @function hasRealTimeGoodFunds
    * @instance
    * @param {STRING} paymentType - The payment type for the current biller we are viewing.
    * @param {OBJECT} branding - The branding property of the model object. 
    * @returns {BOOLEAN} Returns true if "Real time good funds" is on for this type of biller, paper or electronic.
    * @description Based on the paymentType and branding arguments passed, 
    * returns true if "Real time good funds" is on for this type of biller, paper or electronic.
    * @memberof Payment
    */
   static hasRealTimeGoodFunds(paymentType, branding) {
      const isVirtualCard = paymentType === "virtualCard";
      if(isVirtualCard) return false;
      const rushDeliveryType = paymentType === "paper" ? "overnight_check_rxp22" : "same_day_payment_rxp22";
      const rushDeliveryMethodType = paymentType === "paper" ? "paper_biller_rush_delivery_rxp22" : "electronic_biller_rush_delivery_rxp22";
      const { options } = branding;
      const hasRushDelivery = branding.options[rushDeliveryType] === "true";

      if(paymentType === "paper") { 
         const rushDeliveryMethodBranding = options[rushDeliveryMethodType] === "undefined" ?
         "Overnight Check" : options[rushDeliveryMethodType];

         if(hasRushDelivery && rushDeliveryMethodBranding === "Real-Time Good Funds") {
            return true;
         }
      }

      // START: Set the fee date depending on electronic biller branding.
      if(paymentType === "electronic") {
         const rushDeliveryMethodBranding = options[rushDeliveryMethodType] === "undefined" ?
         "Same-Day Payment" : options[rushDeliveryMethodType];

         if(hasRushDelivery && rushDeliveryMethodBranding === "Real-Time Good Funds") {
            return true;
         }
      }

      return false;
   }

   /**
   * @function getElectronicFee
	* @static
	* @memberof Payment
   * @param {OBJECT} branding - The branding property of the model object. 
	* @returns {STRING} - Returns the branded electronic fee.
   * @description Returns the branded electronic fee.
  	* @example Payment.getElectronicFee(branding);
	*/
   static getElectronicFee(branding) {
      const fee = branding.options.same_day_fee_rxp22;
      return fee === "undefined" ? "9.95" : fee;
   }

   /**
   * @function getPaperFee
	* @static
	* @memberof Payment
   * @param {OBJECT} branding - The branding property of the model object. 
	* @returns {STRING} - Returns the branded paper fee.
   * @description Returns the branded paper fee.
  	* @example Payment.getPaperFee(branding);
	*/
   static getPaperFee(branding) {
      const fee = branding.options.overnight_check_fee_rxp22;
      return fee === "undefined" ? "14.95" : fee;
   }
   
   /**
	* @static
	* @memberof Payment
   * @param {payment} Array - an instance of payments class 
	* @returns {payment}
   * @description sorts an array of payment instances in ascending order
  	* @example Payment.sortDateAscending(payments);	
	*/
    static sortDateAscending(payments){
       return payments.sort((a,b)=>{
            if(a.date.isBefore(b.date)){
              return 1;
            }if(b.date.isBefore(a.date)){
              return -1;
            }
            return 0;
          })
    } 

    /**
	* @static
	* @param {payment} Array - an instance of payments class 
	* @returns {payment}
	* @example Payment.sortDateDecending(payments);
	* @description sorts an array of payment instances in decending order
   * @memberof Payment
	*/
    static sortDateDecending(payments){
        return payments.sort((a,b)=>{
             if(a.date.isBefore(b.date)){
               return -1;
             }if(b.date.isBefore(a.date)){
               return 1;
             }
             return 0;
           })
     } 
}

Payment.define({
    "recordType":"payments",
    "recordIDProp":"paymentID",
    "className" : "Payment",
    "recordProperties":[
    /** 
     * @name id
     * @instance
     * @property get {string} - gets id of payment as a string
     * @property set {string|number} - sets the id of payment to a string
     * @memberof Payment 
     */
        BCMSRecordProperty.mapToString("id","_id","paymentID"),
     /** 
     * @name from
     * @instance
     * @memberof Payment     
     * @property getter {string} - gets id of payment as a string
     * @property setter {string|account} - sets the from of payment to an instance of account
     */   
        BCMSRecordProperty.mapToClass("from","_from",()=>Account,"paymentFrom"),
     /** 
     * @name to
     * @instance
     * @description gets id of payment as a string
     * @property getter {string} - gets id of payment as a string
     * @property setter {string|account} - sets the from of payment to an instance of account
     * @memberof Payment 
     */      
        BCMSRecordProperty.mapToClass("to","_to",()=>Biller,"paymentTo"),
      /** 
     * @name amount
     * @instance
     * @description gets id of payment as a string
     * @property getter {string} - gets id of payment as a string
     * @property setter {string|account} - sets the from of payment to an instance of account
     * @memberof Payment 
     */   
        BCMSRecordProperty.mapToCurrency("amount","_amount","paymentAmount"),
     /** 
     * @name status
     * @instance
     * @description gets id of payment as a string
     * @property getter {string} - gets id of payment as a string
     * @property setter {string|account} - sets the from of payment to an instance of account
     * @memberof Payment 
     */   
        BCMSRecordProperty.mapToString("memo","_memo","paymentMemo"),
     /** 
     * @name date
     * @instance
     * @description gets id of payment as a string
     * @property getter {BCMSDate} - gets id of payment as a string
     * @property setter {BCMSDate} - sets the from of payment to an instance of account
     * @memberof Payment 
     */   
        BCMSRecordProperty.mapToDate("date","_date","paymentDate"),
     /** 
     * @name from
     * @instance
     * @description gets id of payment as a string
     * @property getter {string} - gets id of payment as a string
     * @property setter {string|account} - sets the from of payment to an instance of account
     * @memberof Payment 
     */    
        BCMSRecordProperty.mapToString("status","_status","paymentStatus"),
      /** 
     * @name fromAuto
     * @instance
     * @description gets id of payment as a string
     * @property getter {string} - gets id of payment as a string
     * @property setter {string|account} - sets the from of payment to an instance of account
     * @memberof Payment 
     */   
        BCMSRecordProperty.mapToBoolean("fromAuto","_fromAuto","paymentfromAuto"),
      /** 
     * @name isActive
     * @instance
     * @description gets id of payment as a string
     * @property getter {string} - gets id of payment as a string
     * @property setter {string|account} - sets the from of payment to an instance of account
     * @memberof Payment 
     */   
        BCMSRecordProperty.mapToBoolean("isActive","_isActive","paymentIsActive"),
      /** 
     * @name isCheck
     * @instance
     * @description gets id of payment as a string
     * @property getter {string} - gets id of payment as a string
     * @property setter {string|account} - sets the from of payment to an instance of account
     * @memberof Payment 
     */   
        BCMSRecordProperty.mapToBoolean("isCheck","_isCheck","paymentIsCheck"),
      /** 
     * @name message
     * @instance
     * @description gets id of payment as a string
     * @property getter {string} - gets id of payment as a string
     * @property setter {string|account} - sets the from of payment to an instance of account
     * @memberof Payment 
     */   
        BCMSRecordProperty.mapToString("message","_message","paymentMessage"),
      /** 
     * @name confirmationNumber
     * @instance
     * @description gets id of payment as a string
     * @property getter {string} - gets id of payment as a string
     * @property setter {string|account} - sets the from of payment to an instance of account
     * @memberof Payment 
     */   
        BCMSRecordProperty.mapToString("confirmationNumber","_confirmationNumber","paymentConfirmationNumber"),
       
        BCMSRecordProperty.mapToBoolean("hasFee", "_hasFee"),
        BCMSRecordProperty.mapToCurrency("feeAmount", "_feeAmount"),
        BCMSRecordProperty.mapToBoolean("hasRelatedAutopay", "_hasRelatedAutopay", "paymentHasRelatedAutopay"),

      /** 
     * @name autopay
     * @instance
     * @description Sets the autopay instance for this payment. the instance is set either through the model
     * for an existing autopay, or when an autopay is created which would create this payment.
     * @property getter {string} - gets the autopay
     * @property setter {string|autopay} - sets the autopay
     * @memberof Payment 
     */   
      BCMSRecordProperty.mapToClass("autopay", "_autopay", () => Autopay, "paymentAutopay"),

      /** 
     * @name withdrawnOnDate
     * @instance
     * @description gets the withdrawn date of a payment if one was set
     * @property getter {string} - gets the withdrawn date of a payment if one was set
     * @property setter {string|account} - sets the withdrawn date of a payment
     * @memberof Payment 
     */   
      BCMSRecordProperty.mapToDate("withdrawnOnDate", "_withdrawnOnDate", "paymentWithdrawnOnDate"),

      /** 
     * @name hasImmediateWithdrawDate
     * @instance
     * @description gets the value of wether the immediate withdraw date is true or not.
     * @property getter {string} - gets the value of wether the immediate withdraw date is true or not.
     * @property setter {string|account} - sets the value of wether the immediate withdraw date is true or not.
     * @memberof Payment 
     */   
      BCMSRecordProperty.mapToBoolean("hasImmediateWithdrawDate", "_hasImmediateWithdrawDate", "paymentHasImmediateWithdrawDate"),
    ]
})
